import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ModalGlobal } from '../modal/modal.Global';
import { TimelineSettings } from './timeline.Settings';
import { TimelineAll } from './timeline.All';
import { deepCloneArray } from '../../utils/array';
import { update } from '../../services/item';
import {
  canEditItemForItem,
  getId,
  getPicEntryAtIndex,
  getPicEntryById,
  resolvePicAsset,
  getItemPicAsset,
} from '../../utils/item';
import { evaluateMathExpression, processNum } from '../../utils/num';
import useItems from '../../hooks/useItems';
import { saveAll } from '../../store/items';
import { useBoardDrag } from '../../context/boardDrag';
import { buildTimelineEntryLabel } from '../../utils/timeline';

const buildPicSelectionOptions = (targetItem, defaultAsset) => {
  const options = [];
  options.push({
    key: 'default',
    label: "Avatar par défaut",
    previewUrl: defaultAsset?.url || null,
    iconSlug: defaultAsset?.iconSlug || null,
  });
  options.push({
    key: 'none',
    label: 'Aucune image',
    previewUrl: null,
    iconSlug: null,
  });
  (targetItem?.pics || []).forEach((picObj, picIndex) => {
    const picId = getId(picObj);
    const payload = picObj?.[picId] || {};
    options.push({
      key: picId,
      label: payload?.label || `Image ${picIndex + 1}`,
      previewUrl: payload?.url || null,
      iconSlug: payload?.iconSlug || null,
    });
  });
  return options;
};

export const TimelineGlobal = props => {
  const {
    itemId,
    timelineId,
    timeline,
    fieldInGrid,
    canEdit: canEditProp,
    boardType,
  } =
    props;

  const dispatch = useDispatch();
  const item = useSelector(state => state.items[itemId]);
  const items = useSelector(state => state.items);
  const canEdit = canEditProp ?? canEditItemForItem(item, items);

  const [localTimeline, setLocalTimeline] = useState(timeline);
  const { dragData, isDragging, endDrag } = useBoardDrag();
  const [isDragOver, setIsDragOver] = useState(false);

  useEffect(() => {
    setLocalTimeline(timeline);
  }, [timeline]);

  const entryItemIds = useMemo(
    () => localTimeline?.entries?.map(entry => entry.itemId) ?? [],
    [localTimeline?.entries]
  );

  useItems(entryItemIds);

  const updateTimeline = useCallback(updatedTimeline => {
    if (!canEdit) {
      return;
    }
    if (!item?._id) {
      return;
    }
    const nextTimeline = {
      ...localTimeline,
      ...updatedTimeline,
    };
    setLocalTimeline(nextTimeline);

    const clonedTimelines = deepCloneArray(item?.timelines || []);
    let idx = clonedTimelines.findIndex(obj => getId(obj) === timelineId);
    if (idx === -1) {
      clonedTimelines.push({
        [timelineId]: { i: timelineId, label: nextTimeline?.label || 'timeline' },
      });
      idx = clonedTimelines.length - 1;
    }
    clonedTimelines[idx][timelineId] = {
      ...clonedTimelines[idx][timelineId],
      ...nextTimeline,
    };
    update(`items/${itemId}`, 'timelines', clonedTimelines);
  }, [canEdit, item, itemId, localTimeline, timelineId]);

  const updateEntries = useCallback(newEntries => {
    if (!canEdit) {
      return;
    }
    updateTimeline({ entries: newEntries });
  }, [canEdit, updateTimeline]);

  const handleQuickColorChange = (entryIndex, color) => {
    if (!canEdit) {
      return;
    }
    if (!Array.isArray(localTimeline?.entries)) return;
    const updatedEntries = localTimeline.entries.map((entry, idx) =>
      idx === entryIndex ? { ...entry, color } : entry
    );
    updateEntries(updatedEntries);
  };

  const handleEntryPicChange = (entryIndex, picKey) => {
    if (!canEdit) {
      return;
    }
    if (!Array.isArray(localTimeline?.entries)) {
      return;
    }
    const parsedPicId =
      picKey === 'default'
        ? undefined
        : picKey === 'none'
        ? null
        : picKey;
    const updatedEntries = localTimeline.entries.map((entry, idx) =>
      idx === entryIndex ? { ...entry, picId: parsedPicId } : entry
    );
    updateEntries(updatedEntries);
  };

  const handleEntryRemove = entryIndex => {
    if (!canEdit) {
      return;
    }
    if (!Array.isArray(localTimeline?.entries)) {
      return;
    }
    const updatedEntries = localTimeline.entries.filter(
      (_, idx) => idx !== entryIndex
    );
    updateEntries(updatedEntries);
  };

  const handleEntryValueChange = (entryIndex, value) => {
    if (!canEdit) {
      return;
    }
    const entry = localTimeline?.entries?.[entryIndex];
    if (!entry) {
      return;
    }
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return;
    }
    const targetItem = items[entry.itemId];
    if (!targetItem?.has_num) {
      return;
    }
    const numIndex = targetItem.has_num.findIndex(
      numObj => getId(numObj) === entry.numId
    );
    if (numIndex === -1) {
      return;
    }
    const clonedHasNum = deepCloneArray(targetItem.has_num);
    const numId = getId(clonedHasNum[numIndex]);
    const payload = {
      ...(clonedHasNum[numIndex][numId] || {}),
      value: parsed,
    };
    clonedHasNum[numIndex][numId] = payload;
    update(`items/${entry.itemId}`, 'has_num', clonedHasNum);
    dispatch(
      saveAll({
        item: {
          ...targetItem,
          has_num: clonedHasNum,
        },
        log: 'timeline-drag-num',
      })
    );
  };

  const points = useMemo(() => {
    if (!localTimeline?.entries) {
      return [];
    }
    return localTimeline.entries
      .map((entry, index) => {
        const targetItem = items[entry.itemId];
        if (!targetItem) {
          return null;
        }
        const targetNumObj = targetItem.has_num?.find(
          numObj => getId(numObj) === entry.numId
        );
        if (!targetNumObj) {
          return null;
        }
        const numPayload = targetNumObj[entry.numId];
        const value = processNum(
          { num: numPayload, numId: entry.numId, useMod: true, useCon: true },
          targetItem,
          items
        );
        if (Number.isFinite(value) === false) {
          return null;
        }

        const defaultLabel = buildTimelineEntryLabel(
          targetItem?.label,
          numPayload?.label,
          { emptyFallback: entry.label ?? 'Entrée' }
        );
        const storedLabel = typeof entry.label === 'string' ? entry.label : '';
        const hasCustomLabel =
          entry.labelLocked === true ||
          (entry.labelLocked !== true &&
            storedLabel.trim().length > 0 &&
            storedLabel.trim() !== defaultLabel);
        const itemLabel = hasCustomLabel ? storedLabel : defaultLabel;

        let picAsset = { url: null, iconSlug: null };
        if (entry.picId === null) {
          picAsset = { url: null, iconSlug: null };
        } else if (typeof entry.picId === 'string') {
          const targetPicEntry = getPicEntryById(
            targetItem?.pics || [],
            entry.picId
          );
          if (targetPicEntry) {
            picAsset = resolvePicAsset(targetPicEntry);
          } else {
            picAsset = resolvePicAsset(
              getPicEntryAtIndex(targetItem?.pics || [], 0)
            );
          }
        } else {
          picAsset = resolvePicAsset(
            getPicEntryAtIndex(targetItem?.pics || [], 0)
          );
        }
        const picOptions = buildPicSelectionOptions(targetItem, picAsset);
        const picSelectionKey =
          entry.picId === null
            ? 'none'
            : typeof entry.picId === 'string'
            ? entry.picId
            : 'default';

        return {
          id: `${timelineId}-${index}`,
          value,
          itemLabel,
          itemId: entry.itemId,
          numId: entry.numId,
          picUrl: picAsset.url,
          picIconSlug: picAsset.iconSlug,
          color: entry.color,
          size: entry.size || 'medium',
          entryIndex: index,
          picOptions,
          picSelectionKey,
        };
      })
      .filter(Boolean);
  }, [localTimeline?.entries, items, timelineId]);

  const entries = useMemo(
    () => localTimeline?.entries || [],
    [localTimeline?.entries]
  );
  const canAcceptDrag =
    canEdit &&
    isDragging &&
    dragData?.type === 'num' &&
    dragData?.allowTimelineDrop;

  const handlePointerEnter = useCallback(() => {
    if (canAcceptDrag) {
      setIsDragOver(true);
    }
  }, [canAcceptDrag]);

  const handlePointerLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const entryExists = useCallback(
    (targetItemId, targetNumId) =>
      entries.some(
        entry => entry.itemId === targetItemId && entry.numId === targetNumId
      ),
    [entries]
  );

  useEffect(() => {
    if (!canAcceptDrag) {
      setIsDragOver(false);
    }
  }, [canAcceptDrag]);

  const handleDropFromNum = useCallback(() => {
    if (!canAcceptDrag || !dragData) {
      return;
    }
    if (!dragData.itemId || !dragData.numId) {
      endDrag();
      setIsDragOver(false);
      return;
    }
    if (entryExists(dragData.itemId, dragData.numId)) {
      endDrag();
      setIsDragOver(false);
      return;
    }
    const defaultLabel =
      dragData.displayLabel ||
      buildTimelineEntryLabel(dragData.itemLabel, dragData.numLabel, {
        fallbackItemLabel: 'Item',
        fallbackNumLabel: 'Num',
      });
    const avatarAsset = getItemPicAsset(items[dragData.itemId]);
    const defaultColor =
      avatarAsset?.bgColor && avatarAsset.bgColor !== 'transparent'
        ? avatarAsset.bgColor
        : undefined;
    const newEntry = {
      itemId: dragData.itemId,
      numId: dragData.numId,
      label: defaultLabel,
      labelLocked: false,
      color: defaultColor,
    };
    const nextEntries = [...entries, newEntry];
    updateEntries(nextEntries);
    endDrag();
    setIsDragOver(false);
  }, [
    canAcceptDrag,
    dragData,
    endDrag,
    entries,
    entryExists,
    items,
    updateEntries,
  ]);

  return (
    <>
      {canEdit && (
        <ModalGlobal
          idToOpen={timelineId}
          title={localTimeline?.label || 'Timeline'}
        >
          <TimelineSettings
            itemId={itemId}
            timelineId={timelineId}
            timeline={localTimeline}
            onTimelineChange={updateTimeline}
            onEntriesChange={updateEntries}
            onDeleteTimeline={() => {
              if (!canEdit) {
                return;
              }
              const clonedTimelines = deepCloneArray(item?.timelines || []);
              const idx = clonedTimelines.findIndex(
                obj => getId(obj) === timelineId
              );
              if (idx > -1) {
                clonedTimelines.splice(idx, 1);
                setLocalTimeline(undefined);
                update(`items/${itemId}`, 'timelines', clonedTimelines);
              }
            }}
          />
        </ModalGlobal>
      )}

      <div
        className='relative h-full'
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        onPointerUpCapture={handleDropFromNum}
      >
        {canAcceptDrag && (
          <div
            className={`pointer-events-none absolute inset-0 rounded border-2 border-dashed transition ${
              isDragOver
                ? 'border-secondary bg-secondary bg-opacity-10'
                : 'border-contrast border-opacity-20'
            }`}
          >
            <div className='absolute inset-0 flex items-center justify-center px-1 text-center text-xs uppercase tracking-wide text-contrast text-opacity-80'>
              {isDragOver
                ? 'Relâchez pour ajouter ce nombre'
                : 'Glisser un nombre ici'}
            </div>
          </div>
        )}
        {(() => {
          const resolveNumber = value => {
            if (typeof value === 'number' && Number.isFinite(value)) {
              return value;
            }
            if (typeof value === 'string' && value.trim() !== '') {
              const evaluated = evaluateMathExpression(value, item, items);
              return typeof evaluated === 'number' && Number.isFinite(evaluated)
                ? evaluated
                : undefined;
            }
            return undefined;
          };

          const resolvedTickStep = resolveNumber(localTimeline?.tickStep);
          const resolvedMin = resolveNumber(localTimeline?.min);
          const resolvedMax = resolveNumber(localTimeline?.max);
          const forcedRange =
            Number.isFinite(resolvedMin) || Number.isFinite(resolvedMax)
              ? {
                  min: Number.isFinite(resolvedMin) ? resolvedMin : undefined,
                  max: Number.isFinite(resolvedMax) ? resolvedMax : undefined,
                }
              : undefined;

          return (
            <TimelineAll
              order={localTimeline?.order || 'asc'}
              points={points}
              emptyLabel={localTimeline?.emptyLabel}
              showLabels={localTimeline?.showLabels !== false}
              showValues={localTimeline?.showValues !== false}
              showZoom={localTimeline?.showZoom !== false}
              showTicks={localTimeline?.showTicks !== false}
              mode={
                fieldInGrid?.displayType === 'vertical'
                  ? 'vertical'
                  : 'horizontal'
              }
              onChangeEntryColor={handleQuickColorChange}
              onChangeEntryPic={handleEntryPicChange}
              onRemoveEntry={handleEntryRemove}
              tickLabels={localTimeline?.tickLabels}
              tickLabelsVertical={localTimeline?.tickLabelsVertical === true}
              tickStep={
                typeof resolvedTickStep === 'number' && resolvedTickStep > 0
                  ? resolvedTickStep
                  : undefined
              }
              showPointTooltips={localTimeline?.showPointTooltips !== false}
              openDrawerOnClick={localTimeline?.openDrawerOnClick !== false}
              allowPointDrag={
                canEdit && localTimeline?.pointsDraggable !== false
              }
              onChangeEntryValue={handleEntryValueChange}
              range={forcedRange}
              disableLayoutAnimation={boardType === 'draw'}
            />
          );
        })()}
      </div>
    </>
  );
};
